import { Link } from "react-router-dom";
import { Heart, MapPin, BedDouble, Ruler, Video, FileCheck } from "lucide-react";
import { formatINR, formatSqft } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { StreetSnapshot } from "@/components/StreetSnapshot";
import { VerifiedSeal } from "@/components/VerifiedSeal";

export const PropertyCard = ({ property, index = 0, isFavorite = false, onFavoriteChange }) => {
  const { user } = useAuth();
  const [fav, setFav] = useState(isFavorite);
  const [busy, setBusy] = useState(false);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please sign in to save properties");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/favorites", { property_id: property.id });
      setFav(data.favorited);
      onFavoriteChange?.(property.id, data.favorited);
      toast.success(data.favorited ? "Saved to favorites" : "Removed from favorites");
    } catch {
      toast.error("Could not update favorite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link
      to={`/property/${property.id}`}
      className="property-card block fade-in-up"
      style={{ animationDelay: `${Math.min(index, 10) * 60}ms` }}
      data-testid={`property-card-${property.id}`}
    >
      <div className="relative aspect-[4/3] bg-[var(--surface)] overflow-hidden">
        <img
          src={property.images?.[0]}
          alt={property.title}
          className={`w-full h-full object-cover transition-transform duration-700 hover:scale-105 ${property.sold ? "grayscale opacity-70" : ""}`}
          loading="lazy"
        />
        {property.sold && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            data-testid={`sold-badge-${property.id}`}
          >
            <div className="rotate-[-12deg] bg-red-600 text-white font-serif text-3xl tracking-widest px-6 py-2 shadow-xl border-4 border-white/70 uppercase">
              Sold
            </div>
          </div>
        )}
        {property.verification_tier === "document_verified" && property.status === "approved" && !property.sold && (
          <VerifiedSeal size="sm" />
        )}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <span className={`chip ${property.listing_type === "rent" ? "chip-emerald" : "chip-gold"}`}>
            {property.listing_type === "rent" ? "For Rent" : "For Sale"}
          </span>
          {property.verification_tier === "document_verified" && (
            <span className="chip chip-gold text-[10px]" data-testid={`doc-verified-badge-${property.id}`}>
              <FileCheck className="w-3 h-3" /> Document Verified
            </span>
          )}
          {property.virtual_tour_url && (
            <span className="chip chip-emerald text-[10px]" data-testid={`virtual-badge-${property.id}`}>
              <Video className="w-3 h-3" /> 360°
            </span>
          )}
        </div>
        <button
          onClick={toggleFav}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-all"
          data-testid={`favorite-btn-${property.id}`}
          aria-label="Favorite"
        >
          <Heart className={`w-4 h-4 transition-colors ${fav ? "fill-[var(--primary)] text-[var(--primary)]" : "text-[var(--teak)]"}`} />
        </button>
      </div>

      <StreetSnapshot lat={property.lat} lng={property.lng} virtualTourUrl={property.virtual_tour_url} variant="strip" />

      <div className="p-5 space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-xl text-[var(--teak)] leading-tight line-clamp-1" data-testid={`property-title-${property.id}`}>
            {property.title}
          </h3>
        </div>
        <div className="flex items-center gap-1 text-sm text-[var(--ink-muted)]">
          <MapPin className="w-3.5 h-3.5" />
          <span className="truncate">{property.locality}, Mysuru</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-[var(--ink-muted)] pt-1">
          {property.bhk > 0 && (
            <span className="flex items-center gap-1">
              <BedDouble className="w-3.5 h-3.5" /> {property.bhk} BHK
            </span>
          )}
          <span className="flex items-center gap-1">
            <Ruler className="w-3.5 h-3.5" /> {formatSqft(property.area_sqft)}
          </span>
        </div>
        <div className="pt-3 border-t border-[var(--line)] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">
              {property.listing_type === "rent" ? "Monthly Rent" : "Asking Price"}
            </div>
            <div className="font-mono text-lg font-bold text-[var(--primary)]" data-testid={`property-price-${property.id}`}>
              {formatINR(property.price, property.listing_type)}
            </div>
          </div>
          <span className="chip">{property.property_type.replace("_", " ")}</span>
        </div>
      </div>
    </Link>
  );
};
